var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import escapeStringRegexp from 'escape-string-regexp';
import { isCstNode } from 'helpers/chevrotain/cst';
// Given a SHACL prefix, returns a RegExp that can be used for grabbing the
// local name (e.g., the 'NodeShape' in 'sh:NodeShape') from either a full
// SHACL IRI or a prefixed local name.
var getShaclLocalNameMatcher = function (shaclPrefix) {
    return new RegExp("(?:shacl#|" + escapeStringRegexp(shaclPrefix) + ":)(\\S+?)>?$");
};
// Traverses the tree of descendants for a given CstNode until a token is
// found. Returns the "start" token for the CstNode (i.e., the earliest token
// encompassed by the CstNode). This is useful for diagnostics.
var getUnderlyingStartToken = function (ctx) {
    var currentNode = ctx;
    while (currentNode.children) {
        var currentNodeKey = Object.keys(currentNode.children)[0];
        currentNode = currentNode.children[currentNodeKey][0];
    }
    return currentNode;
};
// Grabs the local name (e.g., the 'NodeShape' in 'sh:NodeShape') from either
// a full SHACL IRI or a prefixed local name, if it is available.
var getLocalName = function (iri, matcher) {
    var result = matcher.exec(iri);
    if (result) {
        return result[1];
    }
};
// Given an initial (possibly partially complete) `shape` object and an array
// of CstElements matching the `shaclRulePredicateObjectList` grammar rule,
// transforms the `shape` into a complete `ShaclShape` with associated SHACL
// types and predicates.
// NOTE: mutates `shape`!
var addPredicatesAndTypesToShape = function (shape, shaclRulePredicateObjectListNodes) {
    shaclRulePredicateObjectListNodes.forEach(function (node) {
        var child = node.children[Object.keys(node.children)[0]][0];
        switch (child.name) {
            case 'shaclVerbShape': {
                var token = getUnderlyingStartToken(child);
                var verbTokenInsensitive = token.image.toLowerCase();
                var isTypeVerb = verbTokenInsensitive === 'a' ||
                    verbTokenInsensitive === 'rdf:type' ||
                    verbTokenInsensitive ===
                        '<http://www.w3.org/1999/02/22-rdf-syntax-ns#type>';
                if (!isTypeVerb) {
                    return;
                }
                Object.keys(child.children).forEach(function (key) {
                    if (key !== 'shaclShapeType') {
                        return;
                    }
                    var shapeTypeNode = child.children[key][0];
                    if (shapeTypeNode.children.SHACL_NodeShape) {
                        shape.types.push({
                            type: 'NodeShape',
                            token: getUnderlyingStartToken(shapeTypeNode),
                        });
                    }
                    else if (shapeTypeNode.children.SHACL_PropertyShape) {
                        shape.types.push({
                            type: 'PropertyShape',
                            token: getUnderlyingStartToken(shapeTypeNode),
                        });
                    }
                });
                break;
            }
            case 'shaclPredicateIRI': {
                if (child.children.SingleIriTakingPredicate) {
                    shape.predicates.push({
                        type: 'SingleIriTakingPredicate',
                        token: getUnderlyingStartToken(child.children.SingleIriTakingPredicate[0]),
                    });
                }
                else if (child.children.ManyIriTakingPredicate) {
                    shape.predicates.push({
                        type: 'ManyIriTakingPredicate',
                        token: getUnderlyingStartToken(child.children.ManyIriTakingPredicate[0]),
                    });
                }
                break;
            }
            case 'shaclNodeKind':
                shape.predicates.push({
                    type: 'nodeKind',
                    token: child.children.SHACL_nodeKind[0],
                });
                break;
            case 'shaclTargetNode':
                shape.predicates.push({
                    type: 'targetNode',
                    token: child.children.SHACL_targetNode[0],
                });
                break;
            case 'shaclPropertyPath':
                shape.predicates.push({
                    type: 'path',
                    token: child.children.SHACL_path[0],
                });
                break;
            case 'shaclLiteralConstraint':
                shape.predicates.push({
                    type: 'LiteralConstraint',
                    token: getUnderlyingStartToken(child),
                });
                break;
            case 'shaclListTakingConstraint':
                shape.predicates.push({
                    type: 'ListTakingConstraint',
                    token: getUnderlyingStartToken(child),
                });
                break;
            case 'shaclShapeExpectingConstraint':
                shape.predicates.push({
                    type: 'ShapeExpectingPredicate',
                    token: child.children.ShapeExpectingPredicate[0],
                });
                break;
            case 'shaclHasValueConstraint':
                shape.predicates.push({
                    type: 'hasValue',
                    token: child.children.SHACL_hasValue[0],
                });
                break;
            default:
                console.log("SHACL predicateObjectList with name " + child.name + " not recognized.");
        }
    });
};
// Utility method for constructing a `ShaclShape` from CstElements matching the
// `shaclShape` grammar rule.
var getShaclShapeFromBlankNodePropertyList = function (ctx) {
    var blankNodeNode = ctx.blankNodePropertyList[0];
    var predicateObjectListNode = blankNodeNode.children.predicateObjectList[0];
    var optionalPredicateObjectListNode = ctx.predicateObjectList
        ? ctx.predicateObjectList[0]
        : null;
    if (!isCstNode(predicateObjectListNode) &&
        !isCstNode(optionalPredicateObjectListNode)) {
        return;
    }
    var shaclRulePredicateObjectListNodes = optionalPredicateObjectListNode &&
        optionalPredicateObjectListNode.children &&
        optionalPredicateObjectListNode.children.shaclRulePredicateObjectList
        ? (predicateObjectListNode.children.shaclRulePredicateObjectList ||
            []).concat(optionalPredicateObjectListNode.children
            .shaclRulePredicateObjectList) : predicateObjectListNode.children.shaclRulePredicateObjectList;
    if (!shaclRulePredicateObjectListNodes) {
        return;
    }
    var shape = {
        subject: {
            type: 'blankNodePropertyList',
            token: getUnderlyingStartToken(blankNodeNode),
        },
        types: [],
        predicates: [],
    };
    addPredicatesAndTypesToShape(shape, shaclRulePredicateObjectListNodes);
    return shape;
};
// Returns a new SHACL visitor that extends that given BaseVisitor. The SHACL
// visitor is capable of constructing ShaclShape objects from a given CST and
// then using those shapes to perform validations that cannot be performed in
// the initial parse of a SHACL document.
export var getShaclVisitor = function (BaseVisitor) {
    var ShaclVisitor = /** @class */ (function (_super) {
        __extends(ShaclVisitor, _super);
        function ShaclVisitor() {
            var _this = _super.call(this) || this;
            // `triples` have two alternatives, one with a `subject` and one with a
            // `blankNodePropertyList`. This method constructs SHACL shapes for each
            // alternative.
            _this.triples = function (ctx) {
                if (ctx.subject) {
                    var predicateObjectListNode = ctx.predicateObjectList[0];
                    if (!isCstNode(predicateObjectListNode)) {
                        return;
                    }
                    var shaclRulePredicateObjectListNodes = predicateObjectListNode.children.shaclRulePredicateObjectList;
                    if (!shaclRulePredicateObjectListNodes) {
                        return;
                    }
                    var shape = {
                        subject: {
                            type: Object.keys(ctx.subject[0].children)[0] === 'collection'
                                ? 'collection'
                                : 'subject',
                            token: getUnderlyingStartToken(ctx.subject[0]),
                        },
                        types: [],
                        predicates: [],
                    };
                    addPredicatesAndTypesToShape(shape, shaclRulePredicateObjectListNodes);
                    _this.shapes.push(shape);
                    _this.visit(ctx.subject);
                    _this.visit(predicateObjectListNode);
                }
                else {
                    var shape = getShaclShapeFromBlankNodePropertyList(ctx);
                    if (!shape) {
                        return;
                    }
                    _this.shapes.push(shape);
                    _this.visit(ctx.blankNodePropertyList);
                    if (ctx.predicateObjectList) {
                        _this.visit(ctx.predicateObjectList);
                    }
                }
            };
            // Some SHACL shapes (e.g., nested PropertyShapes) are not matched
            // by the `triples` grammar rule; instead, they match `shapeShape`.
            _this.shaclShape = function (ctx) {
                if (!ctx.blankNodePropertyList) {
                    // Not an inline shape we need to traverse, just an identifier.
                    return;
                }
                var shape = getShaclShapeFromBlankNodePropertyList(ctx);
                if (!shape) {
                    return;
                }
                _this.shapes.push(shape);
                _this.visit(ctx.blankNodePropertyList);
            };
            _this.$resetState = function () {
                _this.shapes = [];
            };
            _this.$validateShapes = function (_a) {
                var shaclPrefix = _a.shacl;
                var validationErrors = [];
                var localNameMatcher = getShaclLocalNameMatcher(shaclPrefix);
                var bnodeCount = 0;
                var shapesConsolidatedBySubject = _this.shapes.reduce(function (consolidatedShapes, shape) {
                    var image = shape.subject.token.image;
                    var subjectImage = image === '[' ? "bnode" + ++bnodeCount : image;
                    if (!consolidatedShapes[subjectImage]) {
                        consolidatedShapes[subjectImage] = {
                            subjects: [shape.subject],
                            types: shape.types,
                            predicates: shape.predicates,
                        };
                    }
                    else {
                        var consolidatedShape = consolidatedShapes[subjectImage];
                        consolidatedShapes[subjectImage] = {
                            subjects: consolidatedShape.subjects.concat([shape.subject]),
                            types: consolidatedShape.types.concat(shape.types),
                            predicates: consolidatedShape.predicates.concat(shape.predicates),
                        };
                    }
                    return consolidatedShapes;
                }, {});
                Object.keys(shapesConsolidatedBySubject).forEach(function (subjectImage) {
                    var _a = shapesConsolidatedBySubject[subjectImage], subjects = _a.subjects, types = _a.types, predicates = _a.predicates;
                    var shapeType;
                    types.forEach(function (_a) {
                        var type = _a.type;
                        if (shapeType && type !== shapeType) {
                            validationErrors.push({
                                name: 'ShapeTypeError',
                                message: 'A SHACL shape can be at most one of NodeShape or PropertyShape.',
                                token: subjects[0].token,
                            });
                        }
                        else if (!shapeType) {
                            shapeType = type;
                        }
                    });
                    var pathPredicates = [];
                    var nonPathPredicateMap = {};
                    predicates.forEach(function (predicate) {
                        var image = predicate.token.image;
                        var localName = getLocalName(image, localNameMatcher);
                        var predicateImage = localName
                            ? shaclPrefix + ":" + localName
                            : image;
                        if (predicateImage === shaclPrefix + ":path") {
                            pathPredicates.push(predicate);
                        }
                        else {
                            if (!nonPathPredicateMap[predicateImage]) {
                                nonPathPredicateMap[predicateImage] = [];
                            }
                            nonPathPredicateMap[predicateImage].push(predicate);
                        }
                    });
                    if (pathPredicates.length > 0) {
                        if (shapeType === 'NodeShape') {
                            validationErrors.push({
                                name: 'ShapePropertyError',
                                message: 'SHACL instances of `NodeShape` cannot have a value for the `path` property.',
                                token: pathPredicates[0].token,
                            });
                        }
                        else {
                            shapeType = 'PropertyShape';
                        }
                        if (pathPredicates.length > 1) {
                            validationErrors.push({
                                name: 'ShapePropertyError',
                                message: 'A shape can have at most one value for sh:path.',
                                token: pathPredicates[1].token,
                            });
                        }
                    }
                    if (shapeType === 'NodeShape') {
                        [
                            'minCount',
                            'maxCount',
                            'uniqueLang',
                            'lessThan',
                            'lessThanOrEquals',
                            'qualifiedValueShape',
                        ].forEach(function (image) {
                            var prefixedImage = shaclPrefix + ":" + image;
                            if (nonPathPredicateMap[prefixedImage]) {
                                validationErrors.push({
                                    name: 'ShapePropertyError',
                                    message: "A NodeShape cannot have any value for " + prefixedImage + ".",
                                    token: nonPathPredicateMap[prefixedImage][0].token,
                                });
                            }
                        });
                    }
                    else {
                        [
                            'deactivated',
                            'severity',
                            'datatype',
                            'nodeKind',
                            'minCount',
                            'maxCount',
                            'minExclusive',
                            'minInclusive',
                            'maxExclusive',
                            'maxInclusive',
                            'minLength',
                            'maxLength',
                            'languageIn',
                            'uniqueLang',
                            'in',
                        ].forEach(function (image) {
                            var prefixedImage = shaclPrefix + ":" + image;
                            if (nonPathPredicateMap[prefixedImage] &&
                                nonPathPredicateMap[prefixedImage].length > 1) {
                                validationErrors.push({
                                    name: 'ShapePropertyError',
                                    message: "A shape can have at most one value for " + prefixedImage + ".",
                                    token: nonPathPredicateMap[prefixedImage][1].token,
                                });
                            }
                        });
                    }
                });
                return { validationErrors: validationErrors };
            };
            _this.validateVisitor();
            _this.shapes = [];
            return _this;
        }
        return ShaclVisitor;
    }(BaseVisitor));
    return new ShaclVisitor();
};
